import {
  CITATIONS_PART_TYPE,
  type CitationsData,
  citationsDataSchema,
  type PassageLocation,
  type PassageProvenance,
  UNSOURCED_MARKER,
  type UnsourcedRange,
} from "@esi/shared";
import type { Block, Inline } from "../components/markdown/parse.ts";
import { t } from "../i18n/i18n.ts";

/**
 * Pure helpers for the cited-answers contract (`@esi/shared` citations.ts): find the citations
 * part, cut a text part into plain runs, citation handles and unsourced markers, and label
 * passage locations. Offsets are UTF-16 code units into the raw text, so nothing here rewrites
 * the text before cutting it: the Markdown renderer places marks and chips by those offsets.
 */

/** The message's citations part (the last one wins), or null while it has not arrived. */
export function citationsOf(parts: readonly { type: string }[]): CitationsData | null {
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i] as { type: string; data?: unknown };
    if (part.type === CITATIONS_PART_TYPE) {
      const parsed = citationsDataSchema.safeParse(part.data);
      return parsed.success ? parsed.data : null;
    }
  }
  return null;
}

export type Token =
  | { kind: "text"; text: string; unsourced: boolean; unverified?: boolean; fromWeb?: boolean }
  | { kind: "cite"; handles: string[]; raw: string }
  | { kind: "unsourced-marker" };

/** `[S1]`, `[W2]`, `[H1]`, `[S1, W3]` (several in one bracket), or the unsourced marker. */
// Case-insensitive, like the server's tokenizer: `[s1]` and `[Unsourced]` count too.
const TOKEN = /\[([SWH]\d+(?:\s*,\s*[SWH]\d+)*)\]|\[unsourced\]/gi;

/**
 * Cuts one text part. `ranges` are the unsourced ranges of this part, `webRanges` its
 * statements citing only web results and `unverifiedRanges` its statements whose citations all
 * failed the check (all already filtered by `textPart`). Markers are never split: only plain
 * runs are cut at range boundaries.
 */
export function tokenize(
  text: string,
  ranges: readonly UnsourcedRange[],
  webRanges: readonly UnsourcedRange[] = [],
  unverifiedRanges: readonly UnsourcedRange[] = [],
): Token[] {
  const tokens: Token[] = [];
  const pushText = (start: number, end: number) => {
    // Cut at every range boundary inside [start, end), then flag each piece.
    const cuts = new Set([start, end]);
    for (const r of [...ranges, ...webRanges, ...unverifiedRanges]) {
      for (const at of [r.start, r.end]) {
        if (at > start && at < end) {
          cuts.add(at);
        }
      }
    }
    const points = [...cuts].sort((a, b) => a - b);
    for (let i = 0; i < points.length - 1; i++) {
      const from = points[i] as number;
      const to = points[i + 1] as number;
      const unsourced = ranges.some((r) => r.start <= from && to <= r.end);
      const fromWeb = !unsourced && webRanges.some((r) => r.start <= from && to <= r.end);
      const unverified = !unsourced && unverifiedRanges.some((r) => r.start <= from && to <= r.end);
      const last = tokens.at(-1);
      if (
        last?.kind === "text" &&
        last.unsourced === unsourced &&
        (last.fromWeb === true) === fromWeb &&
        (last.unverified === true) === unverified
      ) {
        last.text += text.slice(from, to);
      } else {
        tokens.push({
          kind: "text",
          text: text.slice(from, to),
          unsourced,
          ...(fromWeb ? { fromWeb } : {}),
          ...(unverified ? { unverified } : {}),
        });
      }
    }
  };
  let at = 0;
  for (const match of text.matchAll(TOKEN)) {
    const index = match.index ?? 0;
    if (index > at) {
      pushText(at, index);
    }
    if (match[0].toLowerCase() === UNSOURCED_MARKER) {
      tokens.push({ kind: "unsourced-marker" });
    } else {
      const handles = (match[1] ?? "").split(",").map((h) => h.trim().toUpperCase());
      tokens.push({ kind: "cite", handles, raw: match[0] });
    }
    at = index + match[0].length;
  }
  if (at < text.length) {
    pushText(at, text.length);
  }
  return tokens;
}

type TextToken = Extract<Token, { kind: "text" }>;

/**
 * Where the statement a citation closes begins in `run` (the text since the previous citation):
 * after the last sentence end or line break before it, ignoring the punctuation that ends the
 * statement itself ("The torque is 50 Nm. [S1]").
 */
function statementStart(run: string): number {
  const body = run.replace(/\s+$/, "").replace(/[.!?;:]$/, "");
  let start = 0;
  for (const m of body.matchAll(/[.!?](?=\s)|\n/g)) {
    start = (m.index ?? 0) + m[0].length;
  }
  while (start < run.length && /\s/.test(run[start] ?? "")) {
    start++;
  }
  return start;
}

/**
 * Marks as unverified the statement before each citation token whose handles all came back
 * unverified: the text back to the previous citation or sentence boundary. A heuristic, kept
 * subtle in rendering; the chips stay the authority.
 */
export function markUnverified(
  tokens: readonly Token[],
  unverified: (handles: string[]) => boolean,
): Token[] {
  // Citations side by side (`[S1] [S2]`) close one statement together, like `[S1, S2]`: the
  // statement is unverified only if all their handles are.
  const groupHandles = new Map<number, string[]>();
  for (let i = 0; i < tokens.length; i++) {
    if (
      tokens[i]?.kind !== "cite" ||
      tokens[i - 1]?.kind === "cite" ||
      isGap(tokens[i - 1], tokens[i - 2])
    ) {
      continue;
    }
    const handles: string[] = [];
    for (let j = i; j < tokens.length; j++) {
      const t = tokens[j];
      if (t?.kind === "cite") {
        handles.push(...t.handles);
      } else if (!(t?.kind === "text" && t.text.trim() === "" && tokens[j + 1]?.kind === "cite")) {
        break;
      }
    }
    groupHandles.set(i, handles);
  }
  const out: Token[] = [];
  for (const [index, token] of tokens.entries()) {
    const group = groupHandles.get(index);
    if (token.kind === "cite" && group && unverified(group)) {
      let from = out.length;
      while (from > 0 && out[from - 1]?.kind === "text") {
        from--;
      }
      const run = out.slice(from) as TextToken[];
      const start = statementStart(run.map((r) => r.text).join(""));
      const rebuilt: Token[] = [];
      let offset = 0;
      for (const r of run) {
        const end = offset + r.text.length;
        if (end <= start) {
          rebuilt.push(r);
        } else if (offset >= start) {
          rebuilt.push({ ...r, unverified: true });
        } else {
          rebuilt.push({ ...r, text: r.text.slice(0, start - offset) });
          rebuilt.push({ ...r, text: r.text.slice(start - offset), unverified: true });
        }
        offset = end;
      }
      out.splice(from, run.length, ...rebuilt);
    }
    out.push(token);
  }
  return out;
}

/**
 * The statements of one text part to underline as unverified from the check's verdicts per
 * statement (#324): those where every known handle they cite failed. Null when the server sent
 * no per-statement verdicts (older servers and messages): callers then judge a statement by its
 * handles' statuses (`markUnverified`).
 */
export function unverifiedStatements(
  data: CitationsData | null,
  textPart: number,
  text: string,
  known: (handle: string) => boolean,
): UnsourcedRange[] | null {
  if (data?.state !== "done" || !data.unverified) {
    return null;
  }
  return data.unverified.flatMap((r) => {
    if (r.textPart !== textPart) {
      return [];
    }
    const tokens = [...text.slice(r.start, r.end).matchAll(TOKEN)];
    const cited = tokens
      .flatMap((m) => (m[1] ?? "").split(","))
      .map((h) => h.trim().toUpperCase())
      .filter((h) => h !== "" && known(h));
    const last = tokens.at(-1);
    if (!last || cited.length === 0 || !cited.every((h) => r.handles.includes(h))) {
      return [];
    }
    // The statement's words, like `markUnverified`: up to its last citation, without a list,
    // quote or table marker before it, nor the closing punctuation ("Price 70 EUR. [S1]").
    // The closing citations side by side (`[S1][S2]`, `[S1] [S2]`) start where the first does.
    let closing = tokens.length - 1;
    while (closing > 0) {
      const before = tokens[closing - 1] as RegExpExecArray;
      const gap = text.slice(
        r.start + (before.index ?? 0) + before[0].length,
        r.start + ((tokens[closing] as RegExpExecArray).index ?? 0),
      );
      if (!/^[ \t]*$/.test(gap)) {
        break;
      }
      closing--;
    }
    const body = text.slice(r.start, r.start + ((tokens[closing] as RegExpExecArray).index ?? 0));
    const lead = /^(?:\s*(?:[-*+>|]|\d+[.)])\s+)*/.exec(body)?.[0].length ?? 0;
    const end = r.start + body.replace(/[\s.!?;:…]+$/, "").length;
    const start = r.start + lead;
    return end > start ? [{ textPart, start, end }] : [];
  });
}

/** Whether `token` is whitespace between two citations (so the one after it continues a group). */
function isGap(token: Token | undefined, before: Token | undefined): boolean {
  return token?.kind === "text" && token.text.trim() === "" && before?.kind === "cite";
}

/** A raw-offset range of a text part to mark (decorations of the Markdown renderer). */
export interface AnswerMark {
  start: number;
  end: number;
  kind: "unsourced" | "unverified" | "web";
}

/** A citation or unsourced marker at its raw offsets (an atom of the Markdown renderer). */
export interface AnswerAtom {
  start: number;
  end: number;
  token: Exclude<Token, { kind: "text" }>;
}

/**
 * Turns `tokenize` (+ `markUnverified`) output back into raw offsets: the tokens cover the raw
 * text in order, so each one's offset is the sum of the lengths before it. Adjacent text of one
 * kind merges into one mark; unsourced wins over unverified, which wins over "from the web".
 */
export function tokenOffsets(tokens: readonly Token[]): {
  marks: AnswerMark[];
  atoms: AnswerAtom[];
} {
  const marks: AnswerMark[] = [];
  const atoms: AnswerAtom[] = [];
  let at = 0;
  for (const token of tokens) {
    const length =
      token.kind === "text"
        ? token.text.length
        : token.kind === "cite"
          ? token.raw.length
          : UNSOURCED_MARKER.length;
    if (token.kind === "text") {
      const kind = token.unsourced
        ? "unsourced"
        : token.unverified
          ? "unverified"
          : token.fromWeb
            ? "web"
            : null;
      const last = marks.at(-1);
      if (kind && last?.kind === kind && last.end === at) {
        last.end = at + length;
      } else if (kind) {
        marks.push({ start: at, end: at + length, kind });
      }
    } else {
      atoms.push({ start: at, end: at + length, token });
    }
    at += length;
  }
  return { marks, atoms };
}

/**
 * How an atom renders once runs are grouped: the first atom of a run carries the run's
 * de-duplicated known handles; the other atoms of the run, and every atom of a run repeated by
 * the next one in its block, render nothing.
 */
export type AtomPlan = { kind: "run"; handles: string[] } | { kind: "hidden" };

/**
 * The blank run before something not rendered (`start`..`end`), hidden with it so "offered
 * [unsourced]." reads "offered.". Kept when the next word is glued to it: "machines
 * [unsourced]that" reads "machines that". Null when there is no blank to hide.
 */
export function blankBefore(
  text: string,
  start: number,
  end: number,
): { start: number; end: number } | null {
  if (/^[^\s.,;:!?)\]]/.test(text.slice(end, end + 1))) {
    return null;
  }
  let from = start;
  while (from > 0 && (text[from - 1] === " " || text[from - 1] === "\t")) {
    from--;
  }
  return from < start ? { start: from, end: start } : null;
}

/**
 * The atom starts of each Markdown block that holds text (paragraph, heading, quote, list item
 * or table cell), in document order. A list item's nested lists are blocks of their own.
 */
export function blockAtoms(blocks: readonly Block[]): number[][] {
  const out: number[][] = [];
  const visit = (nodes: readonly Inline[], into: number[]) => {
    for (const node of nodes) {
      if (node.type === "atom") {
        into.push(node.start);
      } else if ("children" in node) {
        visit(node.children, into);
      }
    }
  };
  const take = (nodes: readonly Inline[]) => {
    const atoms: number[] = [];
    visit(nodes, atoms);
    if (atoms.length > 0) {
      out.push(atoms);
    }
  };
  const walk = (block: Block) => {
    switch (block.type) {
      case "heading":
      case "paragraph":
      case "quote":
        take(block.content);
        break;
      case "list":
        for (const item of block.items) {
          take(item.content);
          item.children.forEach(walk);
        }
        break;
      case "table":
        for (const cell of [...block.header, ...block.rows.flat()]) {
          take(cell);
        }
        break;
    }
  };
  blocks.forEach(walk);
  return out;
}

/**
 * Groups citation runs for rendering only (tokens, marks and their screen-reader text are still
 * computed from the raw text). Adjacent `cite` atoms separated by spaces form one run whose known
 * handles show once, in order. Within one block, a run whose handle set equals the next run's,
 * with only text between them, is not rendered: the last of such a sequence shows the chips. An
 * unsourced marker or a different set breaks the sequence, and so does a different state of a
 * handle (`stateAt`: one statement's citation failed the check where the next one's passed).
 * `hidden` are the blanks that go with the atoms not rendered, so "one [S2]. Two [S2]." reads
 * "one. Two [S2].".
 */
export function planRuns(
  text: string,
  atoms: readonly AnswerAtom[],
  blocks: readonly number[][],
  known: (handle: string) => boolean,
  stateAt: (handle: string, offset: number) => string = () => "",
): { plan: Map<number, AtomPlan>; hidden: { start: number; end: number }[] } {
  const byStart = new Map(atoms.map((a) => [a.start, a]));
  const plan = new Map<number, AtomPlan>();
  const hidden: { start: number; end: number }[] = [];
  for (const starts of blocks) {
    // Runs and unsourced markers of this block, in order.
    const items: ({ kind: "run"; members: AnswerAtom[]; handles: string[] } | { kind: "mark" })[] =
      [];
    let run: AnswerAtom[] = [];
    const close = () => {
      if (run.length > 0) {
        const handles = [
          ...new Set(run.flatMap((a) => (a.token.kind === "cite" ? a.token.handles : []))),
        ].filter(known);
        items.push({ kind: "run", members: run, handles });
        run = [];
      }
    };
    for (const start of starts) {
      const atom = byStart.get(start);
      if (!atom) {
        continue;
      }
      // A marker, or a bracket citing nothing known (left as its raw text), breaks runs and
      // sequences alike.
      if (atom.token.kind !== "cite" || !atom.token.handles.some(known)) {
        close();
        items.push({ kind: "mark" });
        continue;
      }
      const last = run.at(-1);
      if (last && !/^[ \t]*$/.test(text.slice(last.end, atom.start))) {
        close();
      }
      run.push(atom);
    }
    close();
    for (const [i, item] of items.entries()) {
      if (item.kind !== "run") {
        continue;
      }
      const [first, ...rest] = item.members as [AnswerAtom, ...AnswerAtom[]];
      const next = items[i + 1];
      const repeated =
        next?.kind === "run" &&
        next.handles.length === item.handles.length &&
        [...next.handles].sort().join() === [...item.handles].sort().join() &&
        item.handles.every(
          (h) => stateAt(h, first.start) === stateAt(h, (next.members[0] as AnswerAtom).start),
        );
      if (repeated) {
        plan.set(first.start, { kind: "hidden" });
        const end = (item.members.at(-1) as AnswerAtom).end;
        const blank = blankBefore(text, first.start, end);
        if (blank) {
          hidden.push(blank);
        }
      } else {
        plan.set(first.start, { kind: "run", handles: item.handles });
      }
      let previous = first;
      for (const atom of rest) {
        plan.set(atom.start, { kind: "hidden" });
        if (previous.end < atom.start) {
          hidden.push({ start: previous.end, end: atom.start });
        }
        previous = atom;
      }
    }
  }
  return { plan, hidden };
}

/** Page, section, slide, sheet or image, as one short line. */
export function locationLabel(location: PassageLocation): string {
  if ("page" in location) {
    const section = location.sectionPath?.length ? location.sectionPath.join(" › ") : null;
    const page = t("citations.page", { page: location.page });
    return section ? `${page} · ${section}` : page;
  }
  if ("slide" in location) {
    return t("citations.slide", { slide: location.slide });
  }
  if ("sheet" in location) {
    return `${location.sheet} · ${location.range}`;
  }
  if ("image" in location) {
    return t("citations.image");
  }
  return location.sectionPath.length > 0 ? location.sectionPath.join(" › ") : t("citations.start");
}

/**
 * How the cited passage was produced, when a model read it from a scan or image: "from OCR",
 * "from OCR, check the original" when the transcription guard flagged it, or a caption mark.
 * Null for text read from the file itself.
 */
export function provenanceLabel(provenance: PassageProvenance | null | undefined): string | null {
  if (!provenance) {
    return null;
  }
  if (provenance.source === "caption") {
    return t("citations.caption");
  }
  return provenance.flagged ? t("citations.checkOriginal") : t("citations.fromOcr");
}
